<template>
  <div class="activity-management">
    <el-card class="activity-card">
      <div slot="header" class="card-header">
        <span>体测活动管理</span>
      </div>

      <!-- 筛选区域 -->
      <div class="filter-container">
        <el-row :gutter="20">
          <el-col :span="8">
            <el-input v-model="filterForm.name" placeholder="输入活动名称"></el-input>
          </el-col>
          <el-col :span="8">
            <el-select v-model="filterForm.status" placeholder="选择状态">
              <el-option value="" label="全部状态"></el-option>
              <el-option value="未开始" label="未开始"></el-option>
              <el-option value="进行中" label="进行中"></el-option>
              <el-option value="已结束" label="已结束"></el-option>
            </el-select>
          </el-col>
          <el-col :span="8">
            <el-button type="primary" @click="fetchActivities">查询</el-button>
            <el-button @click="resetFilter">重置</el-button>
            <el-button type="primary" @click="showAddActivityDialog">添加活动</el-button>
          </el-col>
        </el-row>
      </div>

      <!-- 活动表格 -->
      <el-table
        v-loading="loading"
        :data="activities"
        border
        style="width: 100%"
        class="activities-table"
        fit
        header-align="center"
        align="center"
      >
        <el-table-column prop="id" label="活动id" width="80"></el-table-column>
        <el-table-column prop="name" label="活动名称" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column label="开始时间" width="120">
          <template #default="scope">
            {{ formatDate(scope.row.startDate) }}
          </template>
        </el-table-column>
        <el-table-column label="结束时间" width="120">
          <template #default="scope">
            {{ formatDate(scope.row.endDate) }}
          </template>
        </el-table-column>
        <el-table-column prop="location" label="地点" min-width="100" show-overflow-tooltip>
          <template #default="scope">
            {{ scope.row.location || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.status)">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="140" fixed="right">
          <template #default="scope">
            <el-button type="text" @click="showEditActivityDialog(scope.row)">编辑</el-button>
            <el-button type="text" danger @click="deleteActivity(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pageSize"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 添加/编辑活动对话框 -->
    <el-dialog v-model="activityDialogVisible" :title="isEdit ? '编辑活动' : '添加活动'" width="600px">
      <el-form
          ref="activityFormRef"
          :model="activityForm"
          :rules="activityRules"
          label-width="100px"
        >
        <el-form-item label="活动名称" prop="name">
          <el-input v-model="activityForm.name" placeholder="请输入活动名称"></el-input>
        </el-form-item>
        <el-form-item label="地点" prop="location">
          <el-input v-model="activityForm.location" placeholder="请输入活动地点"></el-input>
        </el-form-item>
        <el-form-item label="活动日期" prop="startDate">
          <el-row :gutter="10">
            <el-col :span="11">
              <el-input
                v-model="activityForm.startDate"
                placeholder="开始日期 (YYYY-MM-DD)"
                clearable
                :type="'text'"
                :readonly="false"
              ></el-input>
            </el-col>
            <el-col :span="2" class="text-center">至</el-col>
            <el-col :span="11">
              <el-input
                v-model="activityForm.endDate"
                placeholder="结束日期 (YYYY-MM-DD)"
                clearable
                :type="'text'"
                :readonly="false"
              ></el-input>
            </el-col>
          </el-row>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="activityForm.status" placeholder="请选择状态">
            <el-option value="未开始" label="未开始"></el-option>
            <el-option value="进行中" label="进行中"></el-option>
            <el-option value="已结束" label="已结束"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="activityDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitActivityForm">提交</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick, watch, onUnmounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import axios from 'axios';

// 加载状态
const loading = ref(false);

// 筛选条件
const filterForm = reactive({
  name: '',
  status: ''
});

// 表格数据
const activities = ref([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);

// 对话框状态
const activityDialogVisible = ref(false);
const isEdit = ref(false);

// 活动表单引用 - 使用ref而不是reactive
const activityFormRef = ref(null);

// 活动表单数据 - 重要：使用ref而不是reactive来确保正确的响应式绑定
const activityForm = ref({
  id: '',
  name: '',
  location: '',
  startDate: '',
  endDate: '',
  status: '未开始'
});

// 日期格式验证
const validateDateFormat = (rule, value, callback) => {
  if (!value) {
    return callback(new Error('请输入日期'));
  }
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(value)) {
    return callback(new Error('日期格式应为 YYYY-MM-DD'));
  }
  callback();
};

// 结束日期验证
const validateEndDate = (rule, value, callback) => {
  if (!value) {
    return callback(new Error('请输入结束日期'));
  }
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(value)) {
    return callback(new Error('日期格式应为 YYYY-MM-DD'));
  }
  if (activityForm.value.startDate && value < activityForm.value.startDate) {
    return callback(new Error('结束日期不能早于开始日期'));
  }
  callback();
};

// 获取状态文本
const getStatusText = (status) => {
  // 由于直接使用中文状态值，这里可以直接返回
  return status;
};

// 活动表单验证规则
const activityRules = {
  name: [
    { required: true, message: '请输入活动名称', trigger: 'blur' },
    { min: 2, max: 50, message: '活动名称长度在 2 到 50 个字符', trigger: 'blur' }
  ],
  location: [
    { required: true, message: '请输入活动地点', trigger: 'blur' },
    { min: 1, max: 100, message: '活动地点长度在 1 到 100 个字符', trigger: 'blur' }
  ],
  startDate: [
    { required: true, validator: validateDateFormat, trigger: 'blur' }
  ],
  endDate: [
    { required: true, validator: validateEndDate, trigger: 'blur' }
  ],
  status: [
    { required: true, message: '请选择状态', trigger: 'change' }
  ]
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  // 尝试从字符串中提取年月日部分
  const dateStrClean = String(dateStr);
  
  // 如果包含空格，只取空格前的部分（通常是年月日）
  if (dateStrClean.includes(' ')) {
    return dateStrClean.split(' ')[0];
  }
  
  // 如果包含T，只取T前的部分（ISO格式）
  if (dateStrClean.includes('T')) {
    return dateStrClean.split('T')[0];
  }
  
  // 如果是完整的时间戳格式，尝试提取年月日
  const yearMatch = dateStrClean.match(/\d{4}/);
  const monthMatch = dateStrClean.match(/\d{4}-(\d{2})/);
  const dayMatch = dateStrClean.match(/\d{4}-\d{2}-(\d{2})/);
  
  if (yearMatch && monthMatch && dayMatch) {
    return `${yearMatch[0]}-${monthMatch[1]}-${dayMatch[1]}`;
  }
  
  return dateStrClean;
};
const getStatusType = (status) => {
  switch (status) {
    case '未开始': return 'info';
    case '进行中': return 'success';
    case '已结束': return 'primary';
    default: return '';
  }
};

// 获取活动列表
const fetchActivities = async () => {
  try {
    loading.value = true;
    const params = {
      page: currentPage.value,
      size: pageSize.value,
      name: filterForm.name || undefined,
      status: filterForm.status || undefined
    };

    const response = await axios.get('/admin/activities', { params });
    // 适配后端返回格式 {code, message, data}
    if (response.data.code === 200) {
      activities.value = response.data.data.records || response.data.data || [];
      total.value = response.data.data.total || 0;
    } else {
      ElMessage.error(response.data.message || '获取活动列表失败');
      activities.value = [];
      total.value = 0;
    }
  } catch (error) {
    console.error('获取活动列表异常:', error);
    ElMessage.error('网络错误，请稍后重试');
    activities.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};

// 重置筛选条件
const resetFilter = () => {
  filterForm.name = '';
  filterForm.status = '';
  fetchActivities();
};

// 分页大小变化
const handleSizeChange = (size) => {
  pageSize.value = size;
  fetchActivities();
};

// 当前页码变化
const handleCurrentChange = (current) => {
  currentPage.value = current;
  fetchActivities();
};

// 重置活动表单
const resetActivityForm = () => {
  if (activityFormRef.value) {
    activityFormRef.value.resetFields();
  }
  activityForm.value = {
    id: '',
    name: '',
    location: '',
    startDate: '',
    endDate: '',
    status: '未开始'
  };
};

// 显示添加活动对话框
const showAddActivityDialog = () => {
  isEdit.value = false;
  resetActivityForm();
  // 延迟显示对话框确保DOM更新
  setTimeout(() => {
    activityDialogVisible.value = true;
  }, 0);
};

// 显示编辑活动对话框
const showEditActivityDialog = (activity) => {
  console.log('编辑活动数据:', activity);
  
  // 确保活动数据有效
  if (!activity) {
    ElMessage.error('活动数据无效');
    return;
  }
  
  isEdit.value = true;
  
  // 处理日期 - 只保留YYYY-MM-DD格式
  let startDate = '';
  let endDate = '';
  
  if (activity.startDate) {
    const dateStr = String(activity.startDate);
    // 提取YYYY-MM-DD格式
    if (dateStr.includes('T')) {
      startDate = dateStr.split('T')[0];
    } else if (dateStr.includes(' ')) {
      startDate = dateStr.split(' ')[0];
    } else {
      startDate = dateStr;
    }
  }
  
  if (activity.endDate) {
    const dateStr = String(activity.endDate);
    // 提取YYYY-MM-DD格式
    if (dateStr.includes('T')) {
      endDate = dateStr.split('T')[0];
    } else if (dateStr.includes(' ')) {
      endDate = dateStr.split(' ')[0];
    } else {
      endDate = dateStr;
    }
  }
  
  // 设置表单数据
  activityForm.value = {
    id: activity.id || '',
    name: activity.name || '',
    location: activity.location || '',
    startDate: startDate,
    endDate: endDate,
    status: activity.status || '未开始'
  };
  
  // 直接显示对话框
  activityDialogVisible.value = true;
};

// 提交活动表单
const submitActivityForm = async () => {
  try {
    // 表单验证
    await activityFormRef.value.validate();

    // 构建提交数据
    const submitData = {
      id: activityForm.value.id,
      name: activityForm.value.name.trim(),
      location: activityForm.value.location.trim(),
      status: activityForm.value.status || '未开始',
      startDate: activityForm.value.startDate,
      endDate: activityForm.value.endDate
    };

    console.log('提交数据:', submitData);
    
    let response;
    const requestConfig = {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 10000
    };
    
    if (isEdit.value) {
      // 编辑活动 - 使用PUT请求
      response = await axios.put(`/admin/activities/${activityForm.value.id}`, submitData, requestConfig);
    } else {
      // 添加活动 - 使用POST请求
      response = await axios.post('/admin/activities', submitData, requestConfig);
    }

    // 处理响应
    if (response.data && response.data.code === 200) {
      ElMessage.success(response.data.message || (isEdit.value ? '活动更新成功' : '活动添加成功'));
      activityDialogVisible.value = false;
      fetchActivities(); // 重新获取活动列表
    } else {
      ElMessage.error(response.data.message || (isEdit.value ? '活动更新失败' : '活动添加失败'));
    }
  } catch (error) {
    // 表单验证失败不显示错误提示，由表单组件自动提示
    if (error.name !== 'Error') {
      console.error('提交活动表单异常:', error);
      ElMessage.error('网络错误，请稍后重试');
    }
  }
};

// 删除活动
const deleteActivity = async (activity) => {
  try {
    await ElMessageBox.confirm('确定要删除该活动吗？', '删除确认', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    });

    const response = await axios.delete(`/admin/activities/${activity.id}`);
    if (response.data && response.data.code === 200) {
      ElMessage.success('活动删除成功');
      fetchActivities(); // 重新获取活动列表
    } else {
      ElMessage.error(response.data.message || '活动删除失败');
    }
  } catch (error) {
    if (error === 'cancel') return;
    console.error('删除活动失败:', error);
    ElMessage.error('活动删除失败');
  }
};

// 组件挂载时获取活动列表
onMounted(() => {
  fetchActivities();
});
</script>

<style scoped>
.activity-management {
  padding: 10px 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.add-activity-btn {
  margin-top: 5px;
}

.filter-container {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.activities-table {
  margin-bottom: 20px;
  width: 100%;
}

.activities-table .el-table__cell {
  text-align: center !important;
  justify-content: center;
  align-items: center;
}

.activities-table .el-table__header-wrapper .el-table__header {
  text-align: center;
}

.activities-table .el-tag {
  display: inline-block;
  margin: 0 auto;
}

.pagination-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 15px;
  }
  
  /* 日期输入框样式优化 */
  .text-center {
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>